import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Dialog,
  Grid,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CustomTextField from "../../common/CustomTextField";
import { useForm } from "react-hook-form";
import { getAllActiveOvrAgency, useAgencyReducer } from "../../../redux/slice/agency/AgencySlice";
import CustomDropdownField from "../../common/CustomDropdownFiled";
import { Add, Close } from "@mui/icons-material";
import { useAppDispatch } from "../../../redux/slice/redux-hooks";
import CustomNumberField from "../../common/CustomNumberField";
import { setSingleAlertObj, SingleAlertInfo } from "../../../redux/slice/commonSlice";
import { useModalStyles } from "../../common/useModalStyles";
import { FieldLabel, isOverrideCommissionType, toCommissionTypeRequestKey } from "../../../utils/Utility";
import { useCodetablesSlice } from "../../../redux/slice/codetableSlice";
import { COMMISSION_TYPE_OVERRIDE_KEY } from "../../../constants/applicationConstants";

interface ovrCommissionProps {
  open: boolean;
  handleClose: () => void;
  onAddOvrCommission: (commission: any) => void;
  agencyData?:any
}

const OVRCommissionPopup: React.FC<ovrCommissionProps> = ({
  open,
  handleClose,
  onAddOvrCommission,
  agencyData
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const {
    tokens,
    paperSx,
    headerSx,
    iconBoxSx,
    closeIconSx,
    bodySx,
    footerSx,
    inputFieldSx,
    dropdownWrapSx,
    primaryBtnSx,
    cancelBtnSx,
  } = useModalStyles();
  const { control, handleSubmit, reset, setValue } = useForm<any>({
  defaultValues: {
    agencyId: '',
    commissionRate: ''
  }
});
  const dispatch = useAppDispatch()
  const [selectedOVR, setSelectedOVR] = useState<any>({ label: "", value: "" })
  const { overrideAgencyList } = useAgencyReducer()
  const { allCodeTablesValuesMap } = useCodetablesSlice()

  useEffect(() => {
    dispatch(getAllActiveOvrAgency())
  }, [open])

 const ovrAgencyOptions = useMemo(() => {
  return (overrideAgencyList || []).map((agency: any) => ({
    label: agency.agencyName,
    value: agency.agencyId,
  }));
}, [overrideAgencyList, agencyData]);

  const checkOvrAgencyIsAlreadySelected = (data: any, agencyData: any) => {
    if (data?.agencyId) {
      const test = agencyData.filter((agency: any) =>
        isOverrideCommissionType(allCodeTablesValuesMap, agency.agencyType)
      )
      if (test?.some((agencyData: any) => agencyData?.agencyId === data?.agencyId)) {
        const errorObj: SingleAlertInfo = {
          message: "Selected OVERRIDE Agency is already available.",
          alertType: "error",
        };
        dispatch(setSingleAlertObj(errorObj))
        return true;
      } else {
        return false;
      }
    }
  } 

  const onSubmit = useCallback((data: any) => {
    if (checkOvrAgencyIsAlreadySelected(data, agencyData)) {
      return;
    } else {
      const selectedAgency = ovrAgencyOptions.find(opt => opt.value === data.agencyId);
      const commissionData = {
        agencyId: data?.agencyId,
        agencyName: selectedAgency?.label || "",
        commissionRate: data.commissionRate || 0,
        productCommissionId: null,
        agencyType: toCommissionTypeRequestKey(
          allCodeTablesValuesMap,
          COMMISSION_TYPE_OVERRIDE_KEY
        ),
      }
      onAddOvrCommission(commissionData);
      handleClose();
      const successObj: SingleAlertInfo = {
        message: `${selectedOVR?.label} is added successfully.`,
        alertType: "success",
      };
      dispatch(setSingleAlertObj(successObj))
    }
    // if (productData) {
    //   const selectedWound = woundData?.find((wound: { woundInfoKey: any; }) => wound.woundInfoKey === data.woundName)
    //   const newWound = {
    //     woundDisplayValue: formatWoundName(selectedWound?.woundType, selectedWound?.woundInfoKey),
    //     woundInfoKey: data.woundName
    //   }
    //   const updatedData = { 
    //     ...data,
    //     //CS-1158 
    //     qty:1,
    //     //
    //     selectedProducts: productData,
    //     wound: newWound 
    //   };
    //   onAddProduct(updatedData);
    // }
    // onClose();
  }, [open, selectedOVR,agencyData, allCodeTablesValuesMap]);

  const onCloseAction = () => {
    handleClose();
    reset();
  };
  const title = "Add OVR Service Fee";
  const subtitle = "Choose an override agency and set the service rate.";
  const ariaDescId = "ovr-commission-dialog-description";
  //   const defaultValues = useCallback(() => {
  //     if (defaultValue) {
  //       console.log(defaultValue);
  //       reset(defaultValue);
  //     } else {
  //       // reset();
  //     }
  //   }, [defaultValue, reset]);

  return (
    <Dialog open={open}
      // CS-1213 - Enhancement
      keepMounted
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{ sx: paperSx(isMobile) }}
      onClick={(event) => {
        event.stopPropagation();
      }}
      aria-describedby={ariaDescId} >
      <form onSubmit={handleSubmit(onSubmit)}>
        <Box sx={headerSx}>
          <Box sx={iconBoxSx(tokens.accent)}>
            <Add sx={{ fontSize: "1.2rem", color: tokens.accent }} />
          </Box>
          <Box sx={{ flex: 1, pr: 4 }}>
            <Typography fontWeight={700} sx={{ fontSize: "1.1rem", color: tokens.textPrimary, lineHeight: 1.3 }}>
              {title}
            </Typography>
            <Typography id={ariaDescId} sx={{ fontSize: "0.8rem", color: tokens.textSecondary, mt: 0.15 }}>
              {subtitle}
            </Typography>
          </Box>
          <IconButton onClick={onCloseAction} size="small" sx={closeIconSx} aria-label="Close">
            <Close sx={{ fontSize: "1rem" }} />
          </IconButton>
        </Box>
        <Box sx={bodySx}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Box>
                <FieldLabel label="Override Agency" required tokens={tokens} />
                <Box sx={dropdownWrapSx(false)}>
                  <CustomDropdownField
                    name="agencyId"
                    control={control}
                    options={ovrAgencyOptions}
                    onChangeSelect={(data) => {
                      // Clearing or typing a non-matching value passes no option
                      setSelectedOVR({
                        label: data?.label ?? "",
                        value: data?.value ?? "",
                      });
                    }}
                    label=""
                    size="small"
                    rules={{
                      required: "Override Agency is required."
                    }}
                  />
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Box>
                <FieldLabel label="Service Rate (%)" required tokens={tokens} />
                <Box sx={inputFieldSx(false)}>
                  <CustomTextField
                    control={control}
                    controllerName="commissionRate"
                    label=""
                    size="small"
                    rules={{
                      required: "Service Rate is required.",
                      pattern: {
                        value: /^[0-9]+(\.[0-9]+)?$/,
                        message: "Invalid Service Rate.",
                      },validate: (value:any) =>
                        parseFloat(value) <= 100 || "Service rate cannot be more than 100.",
                    }}
                  />
                </Box>
              </Box>
            </Grid>
            {/* <Grid item xs={12}>
              <CustomTextField
                control={control}
                controllerName="commissionRate"
                label="Service Fee %"
                rules={{
                  required: "Service Rate is required.",
                  pattern: {
                    value: /^[0-9]+(\.[0-9]+)?$/,
                    message: "Invalid Service Rate.",
                  },
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <CustomTextField
                control={control}
                controllerName="commissionPayTerms"
                label="Service Fee Pay Terms "
                rules={{
                  required: "Service Fee Pay Terms is required.",
                }}
              />
            </Grid>
            {isCommTypeOverride && <Grid item xs={12} style={{ marginTop: '5px' }}>
              <CustomDropdownField name="associateType"
                control={control}
                label="Associate Type"
                options={associateOptions}
                rules={{ required: "Associate Type is required." }}
                onChangeSelect={(data) => {
                  if (data) {
                    if (data.value === "PRACTICE") {
                      setSelectedAssociateType(data.value)
                      setValue("associateAgencyId", undefined)
                      setValue("associateAccountRepId", undefined)
                    } else if (data.value === "ACCOUNTREP") {
                      setSelectedAssociateType(data.value)
                      setValue("associateAgencyId", undefined)
                      setValue("associatePracticeId", undefined)
                    } else if (data.value === "AGENCY") {
                      setSelectedAssociateType(data.value)
                      setValue("associatePracticeId", undefined)
                      setValue("associateAccountRepId", undefined)
                    }else {
                      setSelectedAssociateType('')
                      setValue("associateAgencyId", undefined)
                      setValue("associateAccountRepId", undefined)
                      setValue("associatePracticeId", undefined)
                    }
                  } else {
                    setSelectedAssociateType('')
                    setValue("associateAgencyId", undefined)
                    setValue("associateAccountRepId", undefined)
                    setValue("associatePracticeId", undefined)
                  }
                }}
              />
            </Grid>}
            {isCommTypeOverride && (selectedAssociateTypeOption === "PRACTICE" ) && <Grid item xs={12} style={{ marginTop: '5px' }}>
              <CustomDropdownField
                name="associatePracticeId"
                control={control}
                options={practiceListOptions}
                label="Associate Practice"
                rules={{ required: "Associate Practice is required." }}
              />
            </Grid>}
            {isCommTypeOverride && (selectedAssociateTypeOption === "AGENCY") && <Grid item xs={12} style={{ marginTop: '5px' }}>
              <CustomDropdownField
                name="associateAgencyId"
                control={control}
                options={associateAgencyOptions}
                label="Associate Agency"
                rules={{ required: "Associate Agency is required." }}
              />
            </Grid>}
            {isCommTypeOverride && (selectedAssociateTypeOption === "ACCOUNTREP") && <Grid item xs={12} style={{ marginTop: '5px' }}>
              <CustomDropdownField
                name="associateAccountRepId"
                control={control}
                options={agencyExecutivesOptions}
                label="Associate Account Representative"
                rules={{ required: "Associate Account Representative is required." }}
              />
            </Grid>}
            <Grid item xs={12}>
              <CustomDatePicker
                control={control}
                controllerName="effectiveDate"
                label="Effective Date"
                rules={{ required: "Effective Date is required." }}
              />
            </Grid>
            <Grid item xs={12}>
              <CustomDatePicker
                control={control}
                controllerName="inactiveDate"
                label="Inactive Date"
              // rules={{ required: "Inactive Date is required." }}
              />
            </Grid> */}
          </Grid>
        </Box>
        <Box sx={footerSx}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 1.5,
              flexDirection: { xs: "column", sm: "row" },
            }}
          >
            <Box
              component="button"
              type="submit"
              sx={{
                ...primaryBtnSx,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 0.8,
                px: 3,
                py: 1.2,
                border: "none",
                cursor: "pointer",
                color: "#fff",
              }}
            >
              Add Service Fee
            </Box>
            <Box
              component="button"
              type="button"
              onClick={() => onCloseAction()}
              sx={{
                ...cancelBtnSx,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 0.8,
                px: 3,
                py: 1.2,
                border: "1px solid",
                cursor: "pointer",
              }}
            >
              Cancel
            </Box>
          </Box>
        </Box>
      </form>
    </Dialog>
  );
};

export default OVRCommissionPopup;
